import {
  column,
  dateColumn,
  dateFilter,
  DateFilterValue,
  FilterDefs,
  GridColumn,
  GridTableLayout,
  multiFilter,
  numericColumn,
  RowStyles,
  simpleDataRows,
  SimpleHeaderAndData,
  TagType,
  useGridTableLayoutState,
} from "@homebound/beam";
import { sentenceCase } from "change-case";
import { useCallback, useMemo, useState } from "react";
import { dateCell, priceCell, tagCell } from "src/components";
import { DateFilter2, InputMaybe, InvoiceFilter, InvoiceOrder, InvoiceStatus, Order } from "src/generated/graphqlTypes";
import { FinancesPageSharedQuery } from "src/routes/billing/FinancesPage.generated";
import {
  InvoicesPageInvoiceFragment,
  InvoicesPageQuery,
  useInvoicesPageQuery,
} from "src/routes/invoices/InvoicesPage.generated";
import { createInvoiceUrl } from "src/RouteUrls";
import { dateFilterOperations } from "src/utils";
import { parseOrder, toOrder } from "src/utils/ordering";
import { financesDefaultFilter, financesDefaultPage } from "../billing/FinancesPage";

type InvoicesPageProps = { shared: FinancesPageSharedQuery };

type InvoicesPageFilter = Omit<InvoiceFilter, "dueDate"> & { dueDate: DateFilterValue<string> };

export function InvoicesPage({ shared }: InvoicesPageProps) {
  const { markets, projects, internalUsers, currentInternalUser } = shared;
  const currentUserId = currentInternalUser?.id ?? "";
  const [order, setOrder] = useState<InvoiceOrder>({ id: Order.Asc });
  const userOptions = useMemo(
    () => [{ id: currentUserId, name: "Me" }, ...internalUsers.filter(({ id }) => id !== currentUserId)],
    [currentUserId, internalUsers],
  );

  const filterDefs: FilterDefs<InvoicesPageFilter> = useMemo(
    () => ({
      marketId: multiFilter({
        label: "Market",
        options: markets,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      assigneeId: multiFilter({
        label: "Approver",
        options: userOptions,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      status: multiFilter({
        label: "Status",
        options: Object.values(InvoiceStatus).map((status) => ({ value: status, name: sentenceCase(status) })),
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.name,
      }),
      projectId: multiFilter({
        label: "Project",
        options: projects,
        getOptionValue: (o) => o.id,
        getOptionLabel: (o) => o.name,
      }),
      dueDate: dateFilter({
        label: "Due",
        operations: dateFilterOperations,
        getOperationLabel: (o) => o.label,
        getOperationValue: (o) => o.value,
        defaultValue: financesDefaultFilter.dueDate,
      }),
    }),
    [markets, projects, userOptions],
  );
  const layoutState = useGridTableLayoutState<InvoicesPageFilter>({
    persistedFilter: { filterDefs, storageKey: "financesInvoiceFilter" },
    search: "client",
  });
  const query = useInvoicesPageQuery({
    variables: { filter: mapToFilter(layoutState.filter), order, page: financesDefaultPage },
  });
  const maybeFetchNextPage = useCallback(async () => {
    const invoicesPage = query.data?.invoicesPage;
    if (invoicesPage && invoicesPage.entities.length < invoicesPage.pageInfo.totalCount) {
      await query.fetchMore({
        variables: { page: { offset: invoicesPage.entities.length, limit: financesDefaultPage.limit } },
      });
    }
  }, [query]);
  const columns = useMemo(() => createColumns(), []);
  const rowStyles = useMemo(() => createRowStyles(), []);
  const createRows = useCallback(
    (data: InvoicesPageQuery | undefined) => simpleDataRows(data?.invoicesPage.entities ?? []),
    [],
  );

  return (
    <GridTableLayout
      hideEditColumns
      layoutState={layoutState}
      tableProps={{
        query,
        createRows,
        columns,
        rowStyles,
        sorting: {
          on: "server",
          onSort: (key, direction) => setOrder(toOrder(key, direction)),
          value: parseOrder(order),
        },
        as: "virtual",
        style: { rowHeight: "fixed" },
        emptyFallback: "No Invoices found that matched given filters.",
        infiniteScroll: { onEndReached: maybeFetchNextPage },
      }}
    />
  );
}

type Row = SimpleHeaderAndData<InvoicesPageInvoiceFragment>;

function createColumns(): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Project", data: (row) => row.project.name, w: 2, mw: "220px" }),
    column<Row>({ header: "Title", data: (row) => row.title, w: 2, mw: "160px" }),
    numericColumn<Row>({ header: "Number", data: (row) => row.invoiceNumber, w: "140px", mw: "140px" }),
    column<Row>({
      header: "Status",
      data: (row) => tagCell(invoiceStatusToTagType[row.status.code], row.status.name),
      w: "140px",
      mw: "140px",
    }),
    dateColumn<Row>({
      header: "Invoice Date",
      data: (row) => dateCell(row.invoiceDate),
      serverSideSortKey: "invoiceDate",
      w: "120px",
      mw: "120px",
    }),
    dateColumn<Row>({
      header: "Due Date",
      data: (row) => dateCell(row.dueDate),
      serverSideSortKey: "dueDate",
      w: "110px",
      mw: "110px",
    }),
    numericColumn<Row>({
      header: "Invoiced",
      data: (row) => priceCell({ valueInCents: row.amountInCents }),
      w: "110px",
      mw: "110px",
    }),
    numericColumn<Row>({
      header: "Received To Date",
      data: (row) => priceCell({ valueInCents: row.amountPaidInCents }),
      w: "150px",
      mw: "150px",
    }),
    numericColumn<Row>({
      header: "Balance Owed",
      data: (row) => priceCell({ valueInCents: row.balanceInCents }),
      w: "130px",
      mw: "130px",
    }),
    dateColumn<Row>({
      header: "Paid Date",
      data: (row) => dateCell(row.paidDate),
      serverSideSortKey: "paidDate",
      w: "110px",
      mw: "110px",
    }),
  ];
}

function createRowStyles(): RowStyles<Row> {
  return { header: {}, data: { rowLink: ({ data, id }) => createInvoiceUrl(data.project.id, id) } };
}

export const invoiceStatusToTagType: Record<InvoiceStatus, TagType> = {
  [InvoiceStatus.Draft]: "info",
  [InvoiceStatus.Requested]: "update",
  [InvoiceStatus.ChangesRequested]: "warning",
  [InvoiceStatus.Approved]: "success",
  [InvoiceStatus.Paid]: "success",
  [InvoiceStatus.Void]: "neutral",
  [InvoiceStatus.Rejected]: "warning",
};

function mapToFilter(filter: InvoicesPageFilter) {
  const { dueDate, ...others } = filter;
  return {
    ...others,
    ...(dueDate ? { dueDate: { op: dueDate.op, value: dueDate.value } as InputMaybe<DateFilter2> } : {}),
  };
}
